/**
 * Domain configuration for different environments
 * Centralized domain management for easy maintenance
 */

export const DOMAIN_CONFIG = {
  production: {
    dashboard: 'app.adinscene.com',
    api: 'api.adinscene.com',
    cognito: {
      userPoolId: 'eu-west-1_djwcpgcJ8',
      userPoolClientId: '3u3jo05h9i95tjv92asljhv7hl',
    },
  },
  'old-production': {
    dashboard: 'vadds.yorum.io',
    api: 'api-prod-vadds.yorum.io',
    cognito: {
      userPoolId: 'eu-west-1_djwcpgcJ8',
      userPoolClientId: '3u3jo05h9i95tjv92asljhv7hl',
    },
  },
  development: {
    dashboard: 'dev.adinscene.com', 
    api: 'api-dev.adinscene.com',
    cognito: {
      userPoolId: 'eu-west-1_rCaRA66h0', // Updated from latest deployment
      userPoolClientId: '5aulod8is87cfpkp5odagvd1q6', // Updated from latest deployment (2025-06-23)
    },
  },
  localhost: {
    dashboard: 'localhost',
    api: 'localhost:3001', // Use local development server
    cognito: {
      userPoolId: 'eu-west-1_rCaRA66h0', // Use dev Cognito for localhost
      userPoolClientId: '5aulod8is87cfpkp5odagvd1q6', // Updated (2025-06-23)
    },
  }
} as const;

/**
 * Environment detection based on current domain
 */
export const getEnvironment = (): keyof typeof DOMAIN_CONFIG => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    console.log('Current hostname for environment detection:', hostname);
    
    // Check for localhost first
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      console.log('Environment detected: localhost');
      return 'localhost';
    }
    
    // Check for old production domain (vadds.yorum.io)
    if (
      hostname === 'vadds.yorum.io' ||
      hostname === 'dte00925hj847.cloudfront.net' // CloudFront domain for old production
    ) {
      console.log('Environment detected: old-production');
      return 'old-production';
    }
    
    // Check for development domain - exact match or CloudFront domain for dev
    if (
      hostname === DOMAIN_CONFIG.development.dashboard || // Exact match for custom domain
      hostname === 'd2u4v4cr1vea8p.cloudfront.net' || // CloudFront domain for dev
      hostname.includes('dev.adinscene')
    ) {
      console.log('Environment detected: development');
      return 'development';
    }
    
    // Check for production domain - exact match or CloudFront domain for prod
    if (
      hostname === DOMAIN_CONFIG.production.dashboard ||
      hostname.includes('app.adinscene.com')
    ) {
      console.log('Environment detected: production');
      return 'production';
    }
  }
  
  // If we can't detect from hostname, check if we have env variables
  // @ts-ignore - Vite specific environment variables
  const viteEnv = import.meta.env?.VITE_APP_ENVIRONMENT;
  if (viteEnv === 'development') {
    console.log('Environment detected from env vars: development');
    return 'development';
  }
  
  // Fallback to NODE_ENV
  const fallback = process.env.NODE_ENV === 'production' ? 'production' : 'development';
  console.log('Environment fallback to:', fallback);
  return fallback;
};

/**
 * Get API base URL for current environment
 */
export const getApiBaseUrl = (): string => {
  const env = getEnvironment();
  const protocol = env === 'localhost' ? 'http' : 'https';
  return `${protocol}://${DOMAIN_CONFIG[env].api}`;
};

/**
 * Get dashboard URL for current environment
 */
export const getDashboardUrl = (): string => {
  const env = getEnvironment();
  const protocol = env === 'localhost' ? 'http' : 'https';
  const port = env === 'localhost' ? ':9001' : '';
  return `${protocol}://${DOMAIN_CONFIG[env].dashboard}${port}`;
};

/**
 * Get Cognito configuration for current environment
 */
export const getCognitoConfig = () => {
  const env = getEnvironment();
  return DOMAIN_CONFIG[env].cognito;
};

export default DOMAIN_CONFIG;
